import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { compareToBenchmark } from '@/lib/benchmarkData';
import { CURRENCIES } from '@/lib/calculationEngine';
import { ComparisonMetricsCard } from '@/components/benchmark/ComparisonMetricsCard';
import { BenchmarkChart } from '@/components/benchmark/BenchmarkChart';
import { KeyStatsGrid } from '@/components/benchmark/KeyStatsGrid';
import { IndustryInsights } from '@/components/benchmark/IndustryInsights';

export function BenchmarkComparison({ results, config }) {
  const { t } = useTranslation();
  const comparison = useMemo(() => compareToBenchmark(results, config), [results, config]);

  const industryLabel = t('calcData.industries.' + config.industry);
  const sizeLabel = t('calcData.sizes.' + config.companySize);
  const currencySymbol = CURRENCIES.find(c => c.value === config.currency)?.symbol || '$';

  const formatCost = (val) => {
    if (val >= 1000000) return `${currencySymbol}${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${currencySymbol}${(val / 1000).toFixed(0)}K`;
    return `${currencySymbol}${val.toLocaleString()}`;
  };

  return (
    <div className="mt-8 space-y-6" data-testid="benchmark-section">
      <div>
        <h2
          className="text-xl font-semibold text-[#171A1D]"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          data-testid="benchmark-heading"
        >
          {t('benchmark.title')}
        </h2>
        <p className="mt-1 text-sm text-[#66707A]">
          {t('benchmark.adjustedFor')}{' '}
          <span className="text-[#171A1D] font-medium">{industryLabel}</span>,{' '}
          <span className="text-[#171A1D] font-medium">{sizeLabel}</span>.
        </p>
      </div>

      <div
        className="rounded-lg border border-[#DDE1E5] bg-white px-4 py-3.5 flex items-start gap-3"
        data-testid="model-reference-explainer"
      >
        <Info className="w-4 h-4 text-[#2867B2] mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[#171A1D]">{t('benchmark.whatIsTitle')}</p>
          <p className="mt-1 text-sm text-[#3A4249]">{t('benchmark.whatIsBody')}</p>
          <p className="mt-1.5 text-xs text-[#8A929B]" data-testid="model-reference-seo-note">
            {t('benchmark.seoNote')}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <ComparisonMetricsCard
          comparison={comparison}
          industryLabel={industryLabel}
          formatCost={formatCost}
        />
        <div className="space-y-6">
          <BenchmarkChart comparison={comparison} />
          <KeyStatsGrid
            comparison={comparison}
            industryLabel={industryLabel}
            currencySymbol={currencySymbol}
            formatCost={formatCost}
          />
        </div>
      </div>

      <IndustryInsights
        comparison={comparison}
        industryKey={config.industry}
        industryLabel={industryLabel}
      />
    </div>
  );
}
